/**
 * @fileoverview Sistema de monitoreo OAuth2 para Nubelytics
 * 
 * Implementa un sistema de monitoreo avanzado que rastrea el estado del flujo OAuth2,
 * detecta problemas de autenticación y proporciona diagnósticos detallados.
 * Sigue principios SOLID con separación de responsabilidades y patrón Observer.
 * 
 * @author Nubelytics Engineering Team
 * @version 1.0.0
 */

import { EventEmitter } from 'events';

/**
 * Monitor OAuth2 - Rastrea el estado del sistema de autenticación
 * Implementa patrón Observer para notificar cambios de estado
 */
class OAuth2Monitor extends EventEmitter {
  constructor() {
    super();
    this.metrics = {
      tokenRequests: 0,
      tokenRefreshes: 0,
      authErrors: 0,
      lastTokenRefresh: null,
      lastAuthError: null,
      consecutiveErrors: 0,
      systemHealth: 'healthy' // healthy, warning, critical
    };
    
    this.diagnostics = {
      tokenValidation: [],
      networkRequests: [],
      errorHistory: []
    };
    
    // Configuración de umbrales
    this.thresholds = {
      maxConsecutiveErrors: 5,
      maxErrorRate: 0.1, // 10% de error rate
      tokenRefreshInterval: 3600000 // 1 hora en ms
    };
    
    this.startMonitoring();
  }

  /**
   * Inicia el monitoreo del sistema OAuth2
   */
  startMonitoring() {
    // Monitorear cada 30 segundos
    this.monitorInterval = setInterval(() => {
      this.performHealthCheck();
    }, 30000);
    
    // Limpiar diagnósticos antiguos cada hora
    this.cleanupInterval = setInterval(() => {
      this.cleanupOldDiagnostics();
    }, 3600000);
  }

  /**
   * Registra una solicitud de token
   * @param {string} type - Tipo de solicitud (initial, refresh, convert)
   * @param {boolean} success - Si la solicitud fue exitosa
   * @param {Object} metadata - Metadata adicional
   */
  recordTokenRequest(type, success, metadata = {}) {
    this.metrics.tokenRequests++;
    
    if (type === 'refresh') {
      this.metrics.tokenRefreshes++;
      this.metrics.lastTokenRefresh = new Date();
    }
    
    if (!success) {
      this.metrics.authErrors++;
      this.metrics.consecutiveErrors++;
      this.metrics.lastAuthError = new Date();
      
      this.diagnostics.errorHistory.push({
        timestamp: new Date(),
        type: 'token_request_failed',
        requestType: type,
        metadata
      });
    } else {
      this.metrics.consecutiveErrors = 0;
    }
    
    // Registrar en diagnósticos
    this.diagnostics.tokenValidation.push({
      timestamp: new Date(),
      type,
      success,
      metadata
    });
    
    this.updateSystemHealth();
    this.emit('tokenRequest', { type, success, metadata });
  }

  /**
   * Registra una solicitud de red
   * @param {string} url - URL de la solicitud
   * @param {number} status - Código de estado HTTP
   * @param {number} duration - Duración en ms
   */
  recordNetworkRequest(url, status, duration) {
    this.diagnostics.networkRequests.push({
      timestamp: new Date(),
      url,
      status,
      duration,
      success: status >= 200 && status < 300
    });
    
    if (status === 401 || status === 403) {
      this.recordTokenRequest('network_auth_error', false, { url, status });
    }
    
    this.emit('networkRequest', { url, status, duration });
  }

  /**
   * Registra un error de autenticación
   * @param {Error} error - Error ocurrido
   * @param {Object} context - Contexto del error
   */
  recordAuthError(error, context = {}) {
    this.metrics.authErrors++;
    this.metrics.consecutiveErrors++;
    this.metrics.lastAuthError = new Date();
    
    this.diagnostics.errorHistory.push({
      timestamp: new Date(),
      type: 'auth_error',
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      context
    });
    
    this.updateSystemHealth();
    this.emit('authError', { error, context });
  }

  /**
   * Actualiza el estado de salud del sistema
   */
  updateSystemHealth() {
    const errorRate = this.calculateErrorRate();
    const consecutiveErrors = this.metrics.consecutiveErrors;
    
    if (consecutiveErrors >= this.thresholds.maxConsecutiveErrors) {
      this.metrics.systemHealth = 'critical';
    } else if (errorRate > this.thresholds.maxErrorRate || consecutiveErrors > 2) {
      this.metrics.systemHealth = 'warning';
    } else {
      this.metrics.systemHealth = 'healthy';
    }
    
    this.emit('healthChange', this.metrics.systemHealth);
  }

  /**
   * Calcula la tasa de error en la última hora
   * @returns {number} Tasa de error (0-1)
   */
  calculateErrorRate() {
    const oneHourAgo = new Date(Date.now() - 3600000);
    const recentErrors = this.diagnostics.errorHistory.filter(
      error => error.timestamp > oneHourAgo
    );
    const recentRequests = this.diagnostics.tokenValidation.filter(
      request => request.timestamp > oneHourAgo
    );
    
    if (recentRequests.length === 0) return 0;
    return recentErrors.length / recentRequests.length;
  }

  /**
   * Realiza un chequeo de salud del sistema
   */
  performHealthCheck() {
    const now = new Date();
    const lastRefresh = this.metrics.lastTokenRefresh;
    
    // Verificar si hace mucho que no se refresca el token
    if (lastRefresh && (now - lastRefresh) > this.thresholds.tokenRefreshInterval * 2) {
      this.emit('healthWarning', {
        type: 'stale_token',
        message: 'Token no se ha refrescado en mucho tiempo',
        lastRefresh
      });
    }
    
    // Verificar errores consecutivos
    if (this.metrics.consecutiveErrors >= 3) {
      this.emit('healthWarning', {
        type: 'consecutive_errors',
        message: `${this.metrics.consecutiveErrors} errores consecutivos`,
        count: this.metrics.consecutiveErrors
      });
    }
  }

  /**
   * Limpia diagnósticos antiguos para evitar uso excesivo de memoria
   */
  cleanupOldDiagnostics() {
    const cutoff = new Date(Date.now() - 24 * 3600000); // 24 horas
    
    this.diagnostics.tokenValidation = this.diagnostics.tokenValidation.filter(
      item => item.timestamp > cutoff
    );
    
    this.diagnostics.networkRequests = this.diagnostics.networkRequests.filter(
      item => item.timestamp > cutoff
    );
    
    this.diagnostics.errorHistory = this.diagnostics.errorHistory.filter(
      item => item.timestamp > cutoff
    );
  }

  /**
   * Obtiene métricas actuales del sistema
   * @returns {Object} Métricas del sistema
   */
  getMetrics() {
    return {
      ...this.metrics,
      errorRate: this.calculateErrorRate()
    };
  }

  /**
   * Obtiene diagnósticos detallados
   * @returns {Object} Diagnósticos del sistema
   */
  getDiagnostics() {
    return {
      ...this.diagnostics,
      summary: {
        totalTokenRequests: this.metrics.tokenRequests,
        totalRefreshes: this.metrics.tokenRefreshes,
        totalErrors: this.metrics.authErrors,
        errorRate: this.calculateErrorRate(),
        systemHealth: this.metrics.systemHealth
      }
    };
  }

  /**
   * Exporta diagnósticos para análisis externo
   * @returns {string} Diagnósticos en formato JSON
   */
  exportDiagnostics() {
    return JSON.stringify({
      timestamp: new Date(),
      metrics: this.getMetrics(),
      diagnostics: this.getDiagnostics()
    }, null, 2);
  }

  /**
   * Detiene el monitoreo
   */
  stopMonitoring() {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }
}

// Singleton para uso global
const oauth2Monitor = new OAuth2Monitor();

export default oauth2Monitor;
